import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, Navigate, useNavigate } from 'react-router-dom'
import { clearLocallySignedOut } from '@/app/auth/local-logout'
import { adoptShieldTokensFromUrl } from '@/app/auth/shield-token-source'
import { AppShell } from '@/components/app-shell/app-shell'
import { AdminRoute } from '@/components/admin-route'
import { OperatorRoute } from '@/components/operator-route'
import { Protected } from '@/components/protected'
import { Skeleton } from '@/components/ui/skeleton'
import { OrdersRoute } from '@/features/orders/orders-route'
import { JIT_UI_ENABLED } from '@/lib/env'

// The detail route is lazy-loaded — it pulls in the audit timeline and
// the lines table (with catalog fetch) that operators only exercise on
// click.
const OrderDetailRoute = lazy(() =>
  import('@/features/orders/order-detail-route').then(m => ({ default: m.OrderDetailRoute })),
)
const UploadRoute = lazy(() =>
  import('@/features/orders/upload-route').then(m => ({ default: m.UploadRoute })),
)
const BulkWarehouseReleaseRoute = lazy(() =>
  import('@/features/bulk-warehouse-release/bulk-warehouse-release-route').then(m => ({
    default: m.BulkWarehouseReleaseRoute,
  })),
)
const BulkWarehouseReleaseStatusRoute = lazy(() =>
  import('@/features/bulk-warehouse-release/bulk-warehouse-release-status-route').then(m => ({
    default: m.BulkWarehouseReleaseStatusRoute,
  })),
)
const BulkWarehouseReleaseHistoryRoute = lazy(() =>
  import('@/features/bulk-warehouse-release/bulk-warehouse-release-history-route').then(m => ({
    default: m.BulkWarehouseReleaseHistoryRoute,
  })),
)
const FulfillableOrdersRoute = lazy(() =>
  import('@/features/bulk-warehouse-release/fulfillable-orders-route').then(m => ({
    default: m.FulfillableOrdersRoute,
  })),
)
const SalesOrdersRoute = lazy(() =>
  import('@/features/sales-orders/sales-orders-route').then(m => ({
    default: m.SalesOrdersRoute,
  })),
)
const SalesOrderDetailRoute = lazy(() =>
  import('@/features/sales-orders/sales-order-detail-route').then(m => ({
    default: m.SalesOrderDetailRoute,
  })),
)
const ReportsRoute = lazy(() =>
  import('@/features/reports/reports-route').then(m => ({
    default: m.ReportsRoute,
  })),
)
const UploadsRoute = lazy(() =>
  import('@/features/uploads/uploads-route').then(m => ({ default: m.UploadsRoute })),
)
const JitPurchaseOrdersRoute = lazy(() =>
  import('@/features/jit-purchase-orders/jit-purchase-orders-route').then(m => ({
    default: m.JitPurchaseOrdersRoute,
  })),
)
const JitPurchaseOrderDetailRoute = lazy(() =>
  import('@/features/jit-purchase-orders/jit-purchase-order-detail-route').then(m => ({
    default: m.JitPurchaseOrderDetailRoute,
  })),
)
const JitBulkRoute = lazy(() =>
  import('@/features/jit-purchase-orders/jit-bulk-route').then(m => ({
    default: m.JitBulkRoute,
  })),
)
const ChannelSyncsRoute = lazy(() =>
  import('@/features/channel-syncs/channel-syncs-route').then(m => ({
    default: m.ChannelSyncsRoute,
  })),
)

function DetailFallback() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-6 w-64" />
      <Skeleton className="h-4 w-96" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

export function NotFound() {
  return (
    <div className="flex flex-col items-start gap-2 py-16">
      <h1 className="text-[15px] font-semibold">Not found</h1>
      <p className="text-[12.5px] text-[color:var(--muted-foreground)]">
        Nothing lives at this URL.
      </p>
    </div>
  )
}

// v1 URL contract is `/pos` — kept legacy `/orders` redirects so any
// bookmarked links from the v2 preview still resolve.
function LegacyOrdersRedirect() {
  const url = window.location.pathname.replace(/^\/orders(\/|$)/, '/pos$1')
  return <Navigate to={url + window.location.search} replace />
}

export function AuthCallback() {
  const navigate = useNavigate()

  useEffect(() => {
    adoptShieldTokensFromUrl()
    clearLocallySignedOut()
    navigate('/pos', { replace: true })
  }, [navigate])

  return <DetailFallback />
}

export const router = createBrowserRouter([
  {
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  {
    path: '/',
    element: (
      <Protected>
        <AppShell />
      </Protected>
    ),
    children: [
      { index: true, element: <Navigate to="/pos" replace /> },
      { path: 'pos', element: <OrdersRoute /> },
      // Upload and the retired shortlist URLs must stay ahead of
      // :externalOrderNumber so "upload" and "shortlists" are never treated as
      // PO numbers. The shortlist screens are gone — the redirects below only
      // exist so bookmarked links land on /pos instead of a PO lookup.
      {
        path: 'pos/upload',
        element: (
          <Suspense fallback={<DetailFallback />}>
            <UploadRoute />
          </Suspense>
        ),
      },
      {
        path: 'pos/fulfillable',
        element: (
          <OperatorRoute>
            <Suspense fallback={<DetailFallback />}>
              <FulfillableOrdersRoute />
            </Suspense>
          </OperatorRoute>
        ),
      },
      {
        path: 'pos/batches',
        element: (
          <OperatorRoute>
            <Suspense fallback={<DetailFallback />}>
              <BulkWarehouseReleaseHistoryRoute />
            </Suspense>
          </OperatorRoute>
        ),
      },
      {
        path: 'pos/bulk-allocate',
        element: (
          <OperatorRoute>
            <Suspense fallback={<DetailFallback />}>
              <BulkWarehouseReleaseRoute />
            </Suspense>
          </OperatorRoute>
        ),
      },
      {
        path: 'pos/bulk-allocate/:batchId',
        element: (
          <OperatorRoute>
            <Suspense fallback={<DetailFallback />}>
              <BulkWarehouseReleaseStatusRoute />
            </Suspense>
          </OperatorRoute>
        ),
      },
      { path: 'pos/shortlist/new', element: <Navigate to="/pos" replace /> },
      { path: 'pos/shortlists', element: <Navigate to="/pos" replace /> },
      {
        path: 'pos/:externalOrderNumber',
        element: (
          <Suspense fallback={<DetailFallback />}>
            <OrderDetailRoute />
          </Suspense>
        ),
      },
      // The SO queue is its own top-level surface, not a tab under /pos: one
      // row per Vinculum Sales Order rather than per PO, so a PO-scoped URL
      // would misdescribe it. `:soNumber` is the Vision `{PO}-{seq}` and no
      // literal segment lives beneath it, so the ordering hazard the /pos
      // routes carry does not arise here.
      {
        path: 'sales-orders',
        element: (
          <Suspense fallback={<DetailFallback />}>
            <SalesOrdersRoute />
          </Suspense>
        ),
      },
      {
        path: 'sales-orders/:soNumber',
        element: (
          <Suspense fallback={<DetailFallback />}>
            <SalesOrderDetailRoute />
          </Suspense>
        ),
      },
      ...(JIT_UI_ENABLED
        ? [
            {
              path: 'jit-pos',
              element: (
                <OperatorRoute>
                  <Suspense fallback={<DetailFallback />}>
                    <JitPurchaseOrdersRoute />
                  </Suspense>
                </OperatorRoute>
              ),
            },
            {
              path: 'jit-pos/bulk',
              element: (
                <OperatorRoute>
                  <Suspense fallback={<DetailFallback />}>
                    <JitBulkRoute />
                  </Suspense>
                </OperatorRoute>
              ),
            },
            {
              path: 'jit-pos/:workItemId',
              element: (
                <OperatorRoute>
                  <Suspense fallback={<DetailFallback />}>
                    <JitPurchaseOrderDetailRoute />
                  </Suspense>
                </OperatorRoute>
              ),
            },
          ]
        : []),
      {
        path: 'reports',
        element: (
          <Suspense fallback={<DetailFallback />}>
            <ReportsRoute />
          </Suspense>
        ),
      },
      {
        path: 'upload',
        element: (
          <Suspense fallback={<DetailFallback />}>
            <UploadsRoute />
          </Suspense>
        ),
      },
      {
        path: 'admin/channel-syncs',
        element: (
          <AdminRoute>
            <Suspense fallback={<DetailFallback />}>
              <ChannelSyncsRoute />
            </Suspense>
          </AdminRoute>
        ),
      },
      { path: 'orders', element: <LegacyOrdersRedirect /> },
      { path: 'orders/:externalOrderNumber', element: <LegacyOrdersRedirect /> },
      { path: '*', element: <NotFound /> },
    ],
  },
])
